import { useMemo } from "react";
import { useApi, apiResolver } from "@/api/useApi";
import {
  Notice,
  NoticeOutput,
  NoticeOutputNotification,
  NoticeOutputPopup,
} from "./Notice.model";

export type GetNoticesResponse = {
  notices: Notice[];
};

export type GetNoticeByIdResponse = Notice;

export type CreateNoticeRequest = {
  name: string;
  version: string;
};
export type CreateNoticeResponse = Notice;

export type UpdateNoticeRequest = {
  id: string;
  name?: string; // UpdateNoticeDto: optional
  version?: string; // UpdateNoticeDto: optional
};
export type UpdateNoticeResponse = Notice;

export type DeleteNoticeRequest = { id: string };
export type DeleteNoticeResponse = string;

export type CreateNoticeOutputRequest = {
  noticeId: string;
} & (
  | (Omit<NoticeOutputNotification, "id" | "createdAt" | "enabled"> & {
      enabled?: boolean;
    })
  | (Omit<NoticeOutputPopup, "id" | "createdAt" | "enabled"> & {
      enabled?: boolean;
    })
);

export type CreateNoticeOutputResponse = NoticeOutput;

export type UpdateNoticeOutputRequest = {
  noticeId: string;
  noticeOutputId: string;
} & (
  | (Omit<
      NoticeOutputNotification,
      "id" | "createdAt" | "enabled" | "message" | "title" | "name"
    > & {
      enabled?: boolean;
      message?: string;
      title?: string;
      name?: string;
    })
  | (Omit<
      NoticeOutputPopup,
      "id" | "createdAt" | "enabled" | "message" | "title" | "ctaLabel" | "name"
    > & {
      enabled?: boolean;
      message?: string;
      title?: string;
      ctaLabel?: string;
      name?: string;
    })
);

export type UpdateNoticeOutputResponse = NoticeOutput;

// UpdateNoticeOutputsDto: array of CreateNoticeOutputDto union
export type UpdateNoticeOutputsRequest = {
  noticeId: string;
  outputs: CreateNoticeOutputRequest[];
};
export type UpdateNoticeOutputsResponse = { noticeOutputs: NoticeOutput[] };

export type DisableNoticeOutputRequest = {
  noticeId: string;
  noticeOutputId: string;
};
export type DisableNoticeOutputResponse = NoticeOutput;

export type DeleteNoticeOutputRequest = {
  noticeId: string;
  noticeOutputId: string;
};
export type DeleteNoticeOutputResponse = string;

// -------------------------
// URIs (match controller routes)
// -------------------------

const noticeApiUris = {
  base: "/sessions-api/notices",

  // notices
  getNotices: () => `${noticeApiUris.base}`,
  getNoticeById: (id: string) => `${noticeApiUris.base}/${id}`,
  createNotice: () => `${noticeApiUris.base}`,
  updateNotice: (id: string) => `${noticeApiUris.base}/${id}`,
  deleteNotice: (id: string) => `${noticeApiUris.base}/${id}`,

  // notice outputs
  createNoticeOutput: (noticeId: string) =>
    `${noticeApiUris.base}/${noticeId}/outputs`,
  updateNoticeOutputs: (noticeId: string) =>
    `${noticeApiUris.base}/${noticeId}/outputs`,

  updateNoticeOutput: (noticeId: string, noticeOutputId: string) =>
    `${noticeApiUris.base}/${noticeId}/outputs/${noticeOutputId}`,
  disableNoticeOutput: (noticeId: string, noticeOutputId: string) =>
    `${noticeApiUris.base}/${noticeId}/outputs/${noticeOutputId}/disable`,
  deleteNoticeOutput: (noticeId: string, noticeOutputId: string) =>
    `${noticeApiUris.base}/${noticeId}/outputs/${noticeOutputId}`,
};

// -------------------------
// API interface + hook
// -------------------------

export interface NoticeApi {
  getNotices: () => Promise<GetNoticesResponse>;
  getNoticeById: (id: string) => Promise<GetNoticeByIdResponse>;
  createNotice: (request: CreateNoticeRequest) => Promise<CreateNoticeResponse>;
  updateNotice: (request: UpdateNoticeRequest) => Promise<UpdateNoticeResponse>;
  deleteNotice: (request: DeleteNoticeRequest) => Promise<DeleteNoticeResponse>;

  createNoticeOutput: (
    request: CreateNoticeOutputRequest
  ) => Promise<CreateNoticeOutputResponse>;

  updateNoticeOutputs: (
    request: UpdateNoticeOutputsRequest
  ) => Promise<UpdateNoticeOutputsResponse>;

  updateNoticeOutput: (
    request: UpdateNoticeOutputRequest
  ) => Promise<UpdateNoticeOutputResponse>;

  disableNoticeOutput: (
    request: DisableNoticeOutputRequest
  ) => Promise<DisableNoticeOutputResponse>;

  disableAllOutputs: (noticeId: string) => Promise<void>;

  deleteNoticeOutput: (
    request: DeleteNoticeOutputRequest
  ) => Promise<DeleteNoticeOutputResponse>;
}

export function useNoticeApi(): NoticeApi {
  const { api } = useApi();

  return useMemo(
    () => ({
      // notices
      getNotices: async () =>
        apiResolver<GetNoticesResponse>(api.get(noticeApiUris.getNotices())),
      getNoticeById: async (id) =>
        apiResolver<GetNoticeByIdResponse>(
          api.get(noticeApiUris.getNoticeById(id))
        ),
      createNotice: async (body) =>
        apiResolver<CreateNoticeResponse>(
          api.post(noticeApiUris.createNotice(), body)
        ),

      updateNotice: async ({ id, ...body }) =>
        apiResolver<UpdateNoticeResponse>(
          api.put(noticeApiUris.updateNotice(id), body)
        ),

      deleteNotice: async ({ id }) =>
        apiResolver<DeleteNoticeResponse>(
          api.delete(noticeApiUris.deleteNotice(id))
        ),

      // outputs
      createNoticeOutput: async ({ noticeId, ...body }) =>
        apiResolver<CreateNoticeOutputResponse>(
          api.post(noticeApiUris.createNoticeOutput(noticeId), body)
        ),

      updateNoticeOutputs: async ({ noticeId, outputs }) =>
        apiResolver<UpdateNoticeOutputsResponse>(
          api.put(noticeApiUris.updateNoticeOutputs(noticeId), { outputs })
        ),

      updateNoticeOutput: async ({ noticeId, noticeOutputId, ...body }) =>
        apiResolver<UpdateNoticeOutputResponse>(
          api.put(
            noticeApiUris.updateNoticeOutput(noticeId, noticeOutputId),
            body
          )
        ),

      disableNoticeOutput: async ({ noticeId, noticeOutputId }) =>
        apiResolver<DisableNoticeOutputResponse>(
          api.post(noticeApiUris.disableNoticeOutput(noticeId, noticeOutputId))
        ),

      disableAllOutputs: async (noticeId: string) => {
        const notice = await apiResolver<GetNoticeByIdResponse>(
          api.get(noticeApiUris.getNoticeById(noticeId))
        );

        await Promise.all(
          notice.noticeOutputs
            .filter((output) => output.enabled)
            .map((output) =>
              api.post(noticeApiUris.disableNoticeOutput(noticeId, output.id))
            )
        );
      },

      deleteNoticeOutput: async ({ noticeId, noticeOutputId }) =>
        apiResolver<DeleteNoticeOutputResponse>(
          api.delete(noticeApiUris.deleteNoticeOutput(noticeId, noticeOutputId))
        ),
    }),
    [api]
  );
}
